// <s-marquee> — constant-speed helper for the seamless marquee.
// All repetition is done in Liquid (blocks/marquee.liquid): exactly two identical copies, each holding
// the message set several times, translated by -50% with a CSS keyframe (translate3d, linear, infinite).
// This element never adds or removes content. It measures one copy ([data-marquee-track]) and sets the
// pixel distance --marquee-distance = copy width and the duration = distance / speed, so the row moves at a constant pixels-per-second on every screen:
//   data-speed        — px/s from 768px wide (desktop/tablet)
//   data-speed-mobile — px/s below 768px (falls back to data-speed)
// It re-measures whenever the copy's width changes (fonts/images loading, resize) and when the breakpoint
// flips. The duration is written straight onto [data-marquee-inner] (and mirrored into --marquee-duration),
// and the running animation keeps its current position, so a speed change never jumps or leaves a gap.
// Hover pause and [data-paused] pause live in CSS; the loop always runs otherwise. One component per file.
class SMarquee extends HTMLElement {
  #resizeObserver = null;
  #mediaQuery = null;
  #track = null;
  #inner = null;
  #durationMs = 0;
  #distance = 0;
  #onMediaChange = () => this.#update();

  connectedCallback() {
    this.#track = this.querySelector("[data-marquee-track]");
    this.#inner = this.querySelector("[data-marquee-inner]");
    if (!this.#track || !this.#inner) return;

    this.#mediaQuery = window.matchMedia("(min-width: 768px)");
    this.#mediaQuery.addEventListener("change", this.#onMediaChange);

    this.#update();
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this.#track);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#mediaQuery?.removeEventListener("change", this.#onMediaChange);
    this.#mediaQuery = null;
  }

  #speed() {
    const desktop = parseFloat(this.dataset.speed) || 40;
    const mobile = parseFloat(this.dataset.speedMobile) || desktop;
    return this.#mediaQuery && !this.#mediaQuery.matches ? mobile : desktop;
  }

  #update() {
    // One copy's real width in px (fractional, unrounded) is the exact distance of one seamless cycle.
    const distance = this.#track.getBoundingClientRect().width;
    if (distance <= 0) return;
    const nextMs = Math.round((distance / this.#speed()) * 1000);
    if (Math.abs(distance - this.#distance) < 0.5 && Math.abs(nextMs - this.#durationMs) < 20) return;

    // Keep the loop's current position (as a fraction of a cycle) across the change.
    const current = this.#inner.getAnimations ? this.#inner.getAnimations()[0] : null;
    let progress = null;
    if (current && this.#durationMs > 0 && typeof current.currentTime === "number") {
      progress = (current.currentTime % this.#durationMs) / this.#durationMs;
    }

    this.#distance = distance;
    this.#durationMs = nextMs;
    const seconds = `${(nextMs / 1000).toFixed(3)}s`;
    this.style.setProperty("--marquee-duration", seconds);
    this.#inner.style.setProperty("--marquee-distance", `${distance}px`);
    this.#inner.style.animationDuration = seconds;

    // Restart the keyframes so every browser (Safari included) re-resolves the new pixel distance.
    this.#inner.style.animationName = "none";
    void this.#inner.offsetWidth;
    this.#inner.style.animationName = "";

    if (progress !== null) {
      const updated = this.#inner.getAnimations()[0];
      if (updated) updated.currentTime = progress * nextMs;
    }
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
